// The material rate library is shared by every project.
//  - Shared: what the material IS (group, name, unit, assemblies, varieties) and what vendors quote
//    for it (rates, GST, brand, validity, rate history). One document per material in
//    `materialLibrary/{id}`, with the same id in every project.
//  - Per project: what THIS project chose (selected quote, default costing quote), its deal notes, and
//    its own freight/loading for a quote where the site is nearer or further than the quote assumed,
//    in `projects/{projectId}/materialRates/{id}` under the same id.
// The rest of the app keeps working with one merged `MaterialRateItem`.
import type { MaterialRateItem } from '../pages/OfficeEstimationPage';

/** Fields that describe the material itself and are shared by all projects. */
export const MASTER_FIELDS = [
  'group', 'materialName', 'grade', 'diameter', 'coveredDiameters', 'brand', 'unit',
  'secondaryUnitRequired', 'secondaryMultiplier', 'secondaryUnit', 'pieceEntry', 'pieceDimensionUnit', 'codeRequired',
  'trackStock', 'stockPrecision', 'laborStockVisible',
  'assemblyVarietyMap', 'assemblyTypes', 'varieties', 'quoteOptionTemplates', 'order', 'quotes',
] as const;

/** Fields that belong to one project's dealings with vendors. */
export const PROJECT_FIELDS = [
  'quoteOverrides', 'selectedQuoteId', 'selectedOptionQuoteIds', 'defaultCostingQuoteId', 'defaultCostingOptionQuoteIds', 'dealNotes',
] as const;

const masterSet = new Set<string>(MASTER_FIELDS);
const projectSet = new Set<string>(PROJECT_FIELDS);

/** Splits one item's data into its shared part and its per-project part. Other keys (timestamps) go to both. */
export function splitItemData(data: Record<string, any>): { master: Record<string, any>; project: Record<string, any> } {
  const master: Record<string, any> = {};
  const project: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;
    if (masterSet.has(key)) master[key] = value;
    else if (projectSet.has(key)) project[key] = value;
    else { master[key] = value; project[key] = value; }
  }
  return { master, project };
}

// ── Vendor quotes are universal; freight and loading can differ per project ──
export type QuoteOverrides = Record<string, { freight?: number; loading?: number }>;
const money = (value: unknown) => Number(value || 0);

/**
 * The quotes as this project sees them. Quotes live on the shared material; a project that has its own
 * freight or loading for a quote sees that instead. A material moved to the shared library before quotes
 * were universal still has its quotes on the project's rates until the next save moves them.
 */
export function effectiveQuotes(master?: Record<string, any>, rates?: Record<string, any>): any[] {
  const base: any[] = master?.quotes ?? rates?.quotes ?? [];
  const overrides: QuoteOverrides = rates?.quoteOverrides || {};
  return base.map((quote) => overrides[quote?.id] ? { ...quote, ...overrides[quote.id] } : quote);
}

/**
 * Turns the quotes a screen edited (as this project sees them) into what to store: the shared quotes,
 * and this project's overrides. A freight or loading that differs from the shared quote becomes this
 * project's own; one that matches it is not an override. A brand-new quote's freight is the shared one.
 */
export function planQuoteWrite(incoming: any[], master?: Record<string, any>, rates?: Record<string, any>): { quotes: any[]; overrides: QuoteOverrides } {
  const base: any[] = master?.quotes ?? rates?.quotes ?? [];
  const overrides: QuoteOverrides = {};
  const quotes = incoming.map((quote) => {
    const before = base.find((candidate) => candidate?.id === quote?.id);
    if (!before) return quote;
    const own: { freight?: number; loading?: number } = {};
    if (money(quote.freight) !== money(before.freight)) own.freight = money(quote.freight);
    if (money(quote.loading) !== money(before.loading)) own.loading = money(quote.loading);
    if (Object.keys(own).length) overrides[quote.id] = own;
    // Firestore rejects undefined, so a field the shared quote never had is left out.
    const shared = { ...quote };
    (['freight', 'loading'] as const).forEach((field) => { if (before[field] === undefined) delete shared[field]; else shared[field] = before[field]; });
    return shared;
  });
  return { quotes, overrides };
}

/** The merged item every screen works with. A material with no quotes yet has empty quotes. */
export function mergeMaterialItem(id: string, master: Record<string, any>, rates?: Record<string, any>): MaterialRateItem {
  return {
    selectedQuoteId: null,
    selectedOptionQuoteIds: {},
    defaultCostingQuoteId: null,
    defaultCostingOptionQuoteIds: {},
    ...master,
    ...(rates ? pick(rates, PROJECT_FIELDS) : {}),
    quotes: effectiveQuotes(master, rates),
    id,
  } as unknown as MaterialRateItem;
}

function pick(source: Record<string, any>, keys: readonly string[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const key of keys) if (source[key] !== undefined) out[key] = source[key];
  return out;
}

// ── Moving an existing per-project library into the shared one ───────────────
export type LegacyItem = { id: string; data: Record<string, any> };
export type MigrationPlan = {
  create: Array<{ id: string; master: Record<string, any>; project: Record<string, any> }>;
  /** Already in the shared library (a re-run never overwrites anything). */
  skipped: number;
  materials: number;
  quotes: number;
  withNotes: number;
  /** Distinct vendor names on the moved quotes, added to the shared vendor list. */
  vendors: string[];
};

export function planMigration(legacy: LegacyItem[], existingSharedIds: Set<string>): MigrationPlan {
  const create: MigrationPlan['create'] = [];
  let skipped = 0;
  let quotes = 0;
  let withNotes = 0;
  const vendorNames = new Map<string, string>();
  for (const item of legacy) {
    if (existingSharedIds.has(item.id)) { skipped += 1; continue; }
    quotes += Array.isArray(item.data.quotes) ? item.data.quotes.length : 0;
    if (String(item.data.dealNotes || '').trim()) withNotes += 1;
    (Array.isArray(item.data.quotes) ? item.data.quotes : []).forEach((quote: any) => {
      const name = String(quote?.vendor || '').trim();
      const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (key && !vendorNames.has(key)) vendorNames.set(key, name);
    });
    create.push({ id: item.id, master: pick(item.data, [...MASTER_FIELDS, 'createdAt']), project: pick(item.data, PROJECT_FIELDS) });
  }
  return { create, skipped, materials: create.length, quotes, withNotes, vendors: Array.from(vendorNames.values()).sort((a, b) => a.localeCompare(b)) };
}

/** Text of the backup file an admin downloads before migrating. */
export function backupJson(projectId: string, legacy: LegacyItem[]): string {
  return JSON.stringify({ exportedAt: new Date().toISOString(), projectId, count: legacy.length, items: legacy.map((item) => ({ id: item.id, ...item.data })) }, null, 2);
}
